import { useEffect } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { Button } from 'slab'
import { getDropdownCoverageAuditReasons } from 'utils/brokers/coverageAuditReasonUtils'
import { usePostBrokersMembersMemberIdEnrollments } from 'utils/web_api'
import { type PostBrokersMembersMemberIdEnrollmentsCoverageAuditReason } from 'utils/web_api.schemas'

import { ReasonHelpLink } from './brokers/ReasonHelpModal'
import AnimateHeightAndOpacity from './reusable/AnimateHeightAndOpacity'
import { Banner } from './reusable/Banner'
import { FormSelectField, FormTextField } from './reusable/form/FormFields'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from './ui/sheet'

// Available reasons from the Insurance::CoverageAuditLog::Reason enum
// This is automatically synced with the Rails enum via the generated TypeScript types
const COVERAGE_AUDIT_REASONS = [
  { value: '', label: 'Select a reason...' },
  ...getDropdownCoverageAuditReasons()
]

type CreateMemberEnrollmentSheetProps = {
  memberId: string
  planYear: number
  isOpen?: boolean
  isTieredAllowancesEmployer: boolean
  onClose: () => void
  onEnrollmentCreated: () => void
}

type CreateMemberEnrollmentInput = {
  plan_year: number
  category: string
  exchange_status?: string
  plan_code: string
  quoted_premium: string
  coverage_audit_reason: PostBrokersMembersMemberIdEnrollmentsCoverageAuditReason
  max_premium_reimbursement_amount: string
}

const ENROLLMENT_CATEGORIES = [
  { value: 'off_thatch_medical', label: 'Medical' },
  { value: 'off_thatch_dental', label: 'Dental' },
  { value: 'off_thatch_vision', label: 'Vision' },
  { value: 'subsidy', label: 'On-exchange with subsidy' },
  { value: 'medicare', label: 'Medicare' },
]

const EXCHANGE_STATUS_OPTIONS = [
  { value: 'off_exchange', label: 'Off-exchange' },
  { value: 'on_exchange', label: 'On-exchange' },
]

const CreateMemberEnrollmentSheet = ({
  memberId,
  planYear,
  isOpen,
  isTieredAllowancesEmployer,
  onClose,
  onEnrollmentCreated,
}: CreateMemberEnrollmentSheetProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    reset,
  } = useForm<CreateMemberEnrollmentInput>({
    defaultValues: {
      plan_year: planYear,
      category: '',
      exchange_status: 'off_exchange',
      plan_code: '',
      quoted_premium: '',
      coverage_audit_reason: '' as any,
      max_premium_reimbursement_amount: '',
    },
  })

  const createEnrollmentMutation = usePostBrokersMembersMemberIdEnrollments()

  const category = watch('category')
  const coverageAuditReason = watch('coverage_audit_reason')
  const showExchangeStatus = category === 'off_thatch_medical'

  const handleOnOpenChange = (open: boolean) => {
    if (!open) {
      onClose()
      reset()
      createEnrollmentMutation.reset()
    }
  }

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (isOpen) {
        const activeElement = document.activeElement
        const sheetElement = document.querySelector('[data-sheet="create-member-enrollment"]')
        const focusWithinSheet = sheetElement && sheetElement.contains(activeElement)

        // If focus is within the sheet, prevent the Drawer's custom keyboard handling.
        if (focusWithinSheet) {
          // For Tab key, we want normal browser behavior (tabbing between fields).
          if (event.code === 'Tab') {
            event.stopImmediatePropagation() // Prevents other handlers from running.
            return
          }

          // For Space key, we also want normal behavior (e.g., clicking buttons)
          if (event.code === 'Space') {
            event.stopImmediatePropagation()
          }
        }
      }
    }

    document.addEventListener('keydown', handleKeyPress, { capture: true })

    return () => {
      document.removeEventListener('keydown', handleKeyPress, { capture: true })
    }
  }, [isOpen])

  const onSubmit: SubmitHandler<CreateMemberEnrollmentInput> = (data) => {
    // Since the API endpoint only accepts plan_year, we'll just create a basic enrollment
    // and then redirect to the existing form or handle the full creation elsewhere
    createEnrollmentMutation.mutate(
      {
        memberId,
        data: {
          plan_year: data.plan_year,
          category: data.category,
          exchange_status: data.exchange_status,
          plan_code: data.plan_code,
          quoted_premium: data.quoted_premium,
          coverage_audit_reason: data.coverage_audit_reason,
          max_premium_reimbursement_amount: data.max_premium_reimbursement_amount,
        },
      },
      {
        onSuccess: () => {
          handleOnOpenChange(false)
          onEnrollmentCreated()
          toast.success('Enrollment created successfully', {
            position: 'top-center',
          })
        },
        onError: (error: any) => {
          console.error('Failed to create enrollment:', error)
          toast.error('Failed to create enrollment', {
            position: 'top-center',
          })
        },
      }
    )
  }

  return (
    <div>
      <Sheet open={isOpen} onOpenChange={handleOnOpenChange}>
        <SheetContent
          className='h-dvh overflow-y-auto'
          aria-describedby={undefined}
          data-sheet='create-member-enrollment'
        >
          <SheetHeader className='pb-4'>
            <SheetTitle>Create Member {planYear} Enrollment</SheetTitle>
          </SheetHeader>
          <SheetDescription>
            Please note that a Subsidy enrollment will opt the member out of
            ICHRA, any premiums will be deducted from their paycheck.
          </SheetDescription>

          {createEnrollmentMutation.isError && (
            <Banner type='critical' titleText='Unable to create enrollment'>
              {(createEnrollmentMutation.error as any)?.message ||
                'An error occurred while creating the enrollment'}
            </Banner>
          )}
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className='flex flex-col py-6 gap-y-4'>
              <div>
                <div className='flex items-center justify-between'>
                  <label className='block text-xs font-medium text-ds-text-secondary'>
                    Change Reason <span className='text-red-500'>*</span>
                  </label>
                  <ReasonHelpLink />
                </div>
                <FormSelectField<CreateMemberEnrollmentInput>
                  fieldId='coverage_audit_reason'
                  fieldLabel=''
                  fieldOptions={COVERAGE_AUDIT_REASONS}
                  register={register}
                  validationRule={{ required: 'Change Reason is required' }}
                  error={errors.coverage_audit_reason}
                  disabled={createEnrollmentMutation.isPending}
                />
                {coverageAuditReason === 'member_initiated_change' && (
                  <Banner type='warning' titleText='Member initiated changes are rare'>
                    Please confirm whether the member made this change due to a qualifying life event first. Most member-requested changes are actually qualifying life events.
                  </Banner>
                )}
              </div>

              <FormSelectField<CreateMemberEnrollmentInput>
                fieldId='category'
                fieldLabel='Enrollment Type'
                fieldOptions={ENROLLMENT_CATEGORIES}
                register={register}
                validationRule={{ required: 'Enrollment Type is required' }}
                error={errors.category}
                disabled={createEnrollmentMutation.isPending}
              />

              <AnimateHeightAndOpacity isOpen={showExchangeStatus}>
                <FormSelectField<CreateMemberEnrollmentInput>
                  fieldId='exchange_status'
                  fieldLabel='Exchange Status'
                  fieldOptions={EXCHANGE_STATUS_OPTIONS}
                  register={register}
                  validationRule={{ required: 'Exchange Status is required' }}
                  error={errors.exchange_status}
                  disabled={createEnrollmentMutation.isPending}
                />
              </AnimateHeightAndOpacity>

              <FormTextField<CreateMemberEnrollmentInput>
                fieldId='plan_code'
                fieldLabel='Plan Name'
                register={register}
                validationRule={{ required: 'Plan Name is required' }}
                error={errors.plan_code}
                disabled={createEnrollmentMutation.isPending}
              />

              <FormTextField<CreateMemberEnrollmentInput>
                fieldId='quoted_premium'
                fieldLabel='Monthly Premium'
                register={register}
                validationRule={{
                  required: 'Monthly Premium is required',
                  pattern: {
                    value: /^\d+(\.\d{1,2})?$/,
                    message: 'Enter a valid amount',
                  },
                }}
                error={errors.quoted_premium}
                disabled={createEnrollmentMutation.isPending}
              />
              {isTieredAllowancesEmployer && (
                <FormTextField<CreateMemberEnrollmentInput>
                  fieldId='max_premium_reimbursement_amount'
                  fieldLabel='Max premium reimbursement amount: the portion of the premium that the employer will reimburse; usually the self participant portion of the premium'
                  register={register}
                  validationRule={{
                    required: 'Max premium reimbursement amount is required',
                    pattern: {
                      value: /^\d+(\.\d{1,2})?$/,
                      message: 'Enter a valid amount',
                    },
                  }}
                  error={errors.max_premium_reimbursement_amount}
                  disabled={createEnrollmentMutation.isPending}
                />
              )}
            </div>
            <footer className='mt-4 flex justify-end'>
              <Button
                type='submit'
                disabled={createEnrollmentMutation.isPending || !isValid}
                variant='primary'
              >
                {createEnrollmentMutation.isPending
                  ? 'Creating...'
                  : 'Create Enrollment'}
              </Button>
            </footer>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  )
}

export default CreateMemberEnrollmentSheet
